import { isPosReadOnlyEntity } from "./posReadOnly";
import { api } from "../services";

export const DELIVERY_APP_KEYS = ["jahez", "keeta", "ninja", "chefz"];
export const ADDON_APP_KEYS = ["chat_agent", "digital_catalog"];
export const APP_PRICE_KEYS = [...DELIVERY_APP_KEYS, ...ADDON_APP_KEYS];

const EXCLUDED_NAME_PATTERNS = [
  "foodics",
  "tabsense",
  "tab sense",
];

const ADDON_FALLBACK_NAMES = {
  chat_agent: "Order Agent",
  digital_catalog: "Digital Catalog",
};

const APP_ARABIC_NAMES = {
  jahez: "جاهز",
  keeta: "كيتا",
  ninja: "نينجا",
  chefz: "ذا شيفز",
  chat_agent: "وكيل الطلبات",
  digital_catalog: "الكتالوج الرقمي",
};

function collectNameParts(appOrName) {
  if (!appOrName) return [];
  if (typeof appOrName === "string") return [appOrName];
  return [
    appOrName.application,
    appOrName.name,
    appOrName.name_en,
    appOrName.appType,
    appOrName.app,
    appOrName.key,
  ].filter(Boolean);
}

/** Same matching as backend: jahez / keeta / ninja / chefz / chat_agent / digital_catalog. */
export function normalizeDeliveryAppKey(appName) {
  if (!appName) return null;
  const normalized = String(appName)
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (normalized.includes("jahez")) return "jahez";
  if (normalized.includes("keeta")) return "keeta";
  if (normalized.includes("ninja")) return "ninja";
  if (normalized.includes("chefz")) return "chefz";
  if (normalized.includes("chat agent") || normalized.includes("order agent")) {
    return "chat_agent";
  }
  if (normalized.includes("catalog")) return "digital_catalog";
  return null;
}

export function isDeliveryApp(appOrName) {
  const key = collectNameParts(appOrName)
    .map((part) => normalizeDeliveryAppKey(part))
    .find(Boolean);
  return key ? DELIVERY_APP_KEYS.includes(key) : false;
}

export function isPricedAddonApp(appOrName) {
  const key = collectNameParts(appOrName)
    .map((part) => normalizeDeliveryAppKey(part))
    .find(Boolean);
  return key ? ADDON_APP_KEYS.includes(key) : false;
}

function isConnectedStatus(status) {
  if (status == null || status === "") return true;
  return String(status).toLowerCase() === "connected";
}

function isExcludedIntegration(app) {
  const haystack = collectNameParts(app)
    .join(" ")
    .toLowerCase()
    .replace(/[_-]+/g, " ");
  return EXCLUDED_NAME_PATTERNS.some((pattern) => haystack.includes(pattern));
}

function resolveAppKey(app) {
  for (const part of collectNameParts(app)) {
    const key = normalizeDeliveryAppKey(part);
    if (key) return key;
  }
  return null;
}

function toPricedApp(app, key) {
  const name =
    app.application || app.name || ADDON_FALLBACK_NAMES[key] || key;
  // Prefer Arabic name from DB: availableAppId.name_ar (integration apps) or name_ar (addon apps)
  const nameArFromDb =
    app.availableAppId?.name_ar || app.name_ar || null;
  const nameAr = nameArFromDb || APP_ARABIC_NAMES[key] || name;
  return {
    key,
    name,
    nameAr,
    application: name,
    category: ADDON_APP_KEYS.includes(key) ? "ADDON" : "INTEGRATION",
    logo: app.logo || null,
    appType: app.appType || key,
  };
}

function getUniqueConnectedAddonApps(addonApps) {
  if (!Array.isArray(addonApps)) return [];

  const seen = new Set();
  const result = [];

  for (const app of addonApps) {
    if (!app || typeof app !== "object") continue;
    if (app.isConnected !== true && app.isEnabled !== true) continue;
    if (isExcludedIntegration(app)) continue;

    const key = resolveAppKey(app);
    if (!key || !ADDON_APP_KEYS.includes(key) || seen.has(key)) continue;

    seen.add(key);
    result.push(toPricedApp(app, key));
  }

  result.sort(
    (a, b) => ADDON_APP_KEYS.indexOf(a.key) - ADDON_APP_KEYS.indexOf(b.key)
  );
  return result;
}

/**
 * Unique connected delivery apps + enabled add-ons that support product prices.
 * @returns {{ key: string, name: string, application: string }[]}
 */
export function getUniqueConnectedDeliveryApps(apps, addonApps = []) {
  const seen = new Set();
  const result = [];

  if (Array.isArray(apps)) {
    for (const app of apps) {
      if (!app || typeof app !== "object") continue;
      if (!isConnectedStatus(app.status)) continue;
      if (isExcludedIntegration(app)) continue;
      if (!isDeliveryApp(app)) continue;

      const key = resolveAppKey(app);
      if (!key || !DELIVERY_APP_KEYS.includes(key) || seen.has(key)) continue;

      seen.add(key);
      result.push(toPricedApp(app, key));
    }
  }

  result.sort(
    (a, b) => DELIVERY_APP_KEYS.indexOf(a.key) - DELIVERY_APP_KEYS.indexOf(b.key)
  );

  for (const addon of getUniqueConnectedAddonApps(addonApps)) {
    if (seen.has(addon.key)) continue;
    seen.add(addon.key);
    result.push(addon);
  }

  return result;
}

/** Load delivery integrations and enabled add-ons used for product prices. */
export async function fetchPricedAppSources() {
  const token = localStorage.getItem("token");
  if (!token) return { apps: [], addonApps: [] };
  const headers = { Authorization: `Bearer ${token}` };
  const [appsResult, addonsResult] = await Promise.allSettled([
    api.get("/apps", { headers }),
    api.get("/available-apps/categorized", { headers }),
  ]);
  return {
    apps:
      appsResult.status === "fulfilled" ? appsResult.value.data?.apps || [] : [],
    addonApps:
      addonsResult.status === "fulfilled"
        ? addonsResult.value.data?.categories?.addOns?.apps || []
        : [],
    appsError:
      appsResult.status === "rejected" ? appsResult.reason : null,
  };
}

export function getProductPriceForApp(product, appName) {
  if (!product) return 0;
  const key = normalizeDeliveryAppKey(appName);
  const overrides =
    product.app_prices && typeof product.app_prices === "object"
      ? product.app_prices
      : {};
  const override = key ? overrides[key] : undefined;
  if (override != null && override !== "") {
    const parsed = Number(override);
    if (Number.isFinite(parsed)) return parsed;
  }
  const fallback = Number(product.price);
  return Number.isFinite(fallback) ? fallback : 0;
}

export function shouldShowAppPriceIcon(product, deliveryApps) {
  if (isPosReadOnlyEntity(product)) return false;
  if (product?.is_foodics === true || product?.is_tabsense === true) return false;
  return Array.isArray(deliveryApps) && deliveryApps.length > 0;
}

export function hasAppPriceOverride(product, appName) {
  if (!product?.app_prices || typeof product.app_prices !== "object") return false;
  const key = normalizeDeliveryAppKey(appName);
  if (!key) return false;
  const override = product.app_prices[key];
  if (override == null || override === "") return false;
  return Number.isFinite(Number(override));
}

export function getDisplayedAppPriceValue(values, appKey, defaultPrice) {
  const raw = values?.[appKey];
  if (raw !== undefined && raw !== "") return String(raw);
  if (defaultPrice === undefined || defaultPrice === null || defaultPrice === "") {
    return "";
  }
  return String(defaultPrice);
}

/** Initial editor state from saved product.app_prices. */
export function getAppPriceEditorState(product, deliveryApps = []) {
  const overrides =
    product?.app_prices && typeof product.app_prices === "object"
      ? product.app_prices
      : {};
  const values = {};
  const useDefault = {};

  for (const app of deliveryApps) {
    if (!app?.key) continue;
    const override = overrides[app.key];
    const parsed = Number(override);
    const has =
      override != null &&
      override !== "" &&
      Number.isFinite(parsed) &&
      parsed > 0;
    useDefault[app.key] = !has;
    values[app.key] = has ? String(override) : "";
  }

  return {
    customPricingEnabled: true,
    values,
    useDefault,
  };
}

/** Persist only explicit platform overrides. Default-price platforms are omitted. */
export function buildAppPricesFromEditor({
  deliveryApps,
  values,
  useDefault = {},
  customPricingEnabled = true,
}) {
  if (!customPricingEnabled || !Array.isArray(deliveryApps)) return {};
  const result = {};
  for (const app of deliveryApps) {
    if (!app?.key || useDefault[app.key] !== false) continue;
    const parsed = Number(values?.[app.key]);
    if (Number.isFinite(parsed) && parsed > 0) {
      result[app.key] = parsed;
    }
  }
  return result;
}

export function sanitizeDecimalPriceInput(value) {
  let cleaned = String(value ?? "").replace(/[^0-9.]/g, "");
  const decimalCount = (cleaned.match(/\./g) || []).length;
  if (decimalCount > 1) return null;
  const parts = cleaned.split(".");
  if (parts[1] && parts[1].length > 2) return null;
  if (
    cleaned.length > 1 &&
    cleaned[0] === "0" &&
    cleaned[1] !== "."
  ) {
    return null;
  }
  return cleaned;
}
